import CONSTANTS from '../Constants'
import HTTP_STATUS_CODE from '../Constants/HttpStatusCode'
import { SEGMENT_MANAGEMENT_CRUD } from '../Constants/ApiDefinations'
import mainApiService from '../Services'
import { normalizeSegmentPageResponse } from '../Components/Atoms/DynamicFormCreation/complexFieldHelpers'
import { RowSegment } from '../Models/ManageContainer'

/** Returns true when the given master URL is the segment API. */
export const isSegmentMaster = (master?: string): boolean =>
  !!master?.includes(CONSTANTS.SEGMENT_MASTER_URL)

/**
 * Returns true when the given master URL is registered for API lazy loading.
 * To enable lazy loading for a new field, add its master URL to
 * CONSTANTS.LAZY_LOAD_FIELD_MASTERS — no other change needed.
 */
export const isLazyLoadField = (master?: string): boolean =>
  !!master && CONSTANTS.LAZY_LOAD_FIELD_MASTERS.some((url) => master.includes(url))

const buildSegmentQuery = (
  page: number,
  limit: number,
  searchText: string,
  module?: string,
): string => {
  const query = new URLSearchParams()
  query.append('page', String(page))
  query.append('limit', String(limit))
  if (searchText?.trim()) query.append('searchText', searchText.trim())
  if (module) query.append('module', module)
  return `?${query.toString()}`
}

/**
 * Builds a paginated fetch function for the segment list API.
 * Can be used by any component in the system — pass the result as the
 * `fetchPage` prop to MUIAutocompleteMultiSelect or MUIAutocompleteSingleSelect.
 *
 * @example
 * <MUIAutocompleteMultiSelect
 *   enableLazyLoad
 *   fetchPage={buildSegmentFetchPage('widget')}
 * />
 */
export const buildSegmentFetchPage =
  (module?: string) =>
  async (page: number, pageSize: number, searchText: string, signal: AbortSignal) => {
    const queryString = buildSegmentQuery(page, pageSize, searchText, module)
    const reqData: any = SEGMENT_MANAGEMENT_CRUD.GET_ALL_SEGMENT_NAME(queryString)
    const result: any = await mainApiService({ ...reqData, signal })
    const isSuccess = result?.responseCode === HTTP_STATUS_CODE.OK || Array.isArray(result?.data)
    if (isSuccess && !result?.error) {
      return normalizeSegmentPageResponse(result)
    }
    const message = result?.error?.message ?? 'Request failed'
    throw new Error(message)
  }

export const buildSegmentPayload = (segmentList: Partial<RowSegment>[]) => {
  return (segmentList || []).map((segment) => ({
    segmentId: segment.segmentId,
    segmentKey: segment.key || segment.segmentKey,
    value: segment.value,
  }))
}
